import { useState, useCallback } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateCollection } from "@/hooks/useCollections";
import type { CollectionVisibility } from "@/types/collections";
import { CAPTION, CONTROL, HEADLINE, LABEL } from "@/lib/typography";

interface CreateCollectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after collection is successfully created (before navigation). */
  onCreated?: (collectionId: string, title: string, visibility: CollectionVisibility) => void;
}

export function CreateCollectionSheet({
  open,
  onOpenChange,
  onCreated,
}: CreateCollectionSheetProps) {
  const createMutation = useCreateCollection();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<CollectionVisibility>("private");

  const handleCreate = useCallback(() => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    createMutation.mutate(
      {
        title: trimmedTitle,
        description: description.trim() || undefined,
        visibility,
      },
      {
        onSuccess: (created) => {
          toast.success(`Created "${trimmedTitle}"`);
          onOpenChange(false);
          setTitle("");
          setDescription("");
          onCreated?.(created.collection_id, trimmedTitle, visibility);
        },
      },
    );
  }, [title, description, visibility, createMutation, onOpenChange, onCreated]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setTitle("");
        setDescription("");
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent>
        <DrawerTitle className={HEADLINE}>New collection</DrawerTitle>

        <div className="mt-4 space-y-4">
          {/* Title */}
          <div>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Collection name"
              aria-label="Collection name"
              maxLength={100}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
            />
          </div>

          {/* Description */}
          <div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description (optional)"
              maxLength={500}
              rows={2}
              className="text-ui-field w-full resize-none rounded-md border border-input bg-background px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Visibility */}
          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <span className={cn(LABEL, "text-muted-foreground")}>Visibility</span>
              <span className={CAPTION}>
                {visibility === "private"
                  ? "Only you and invited viewers/collaborators"
                  : "Anyone can follow a public collection"}
              </span>
            </div>
            <div className="flex gap-2">
              {(["private", "public"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVisibility(v)}
                  className={cn(
                    CONTROL,
                    "flex-1 rounded-lg border px-3 py-2 capitalize transition-colors",
                    visibility === v
                      ? "border-brand-accent bg-brand-accent/10 text-brand-accent"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <Button
            variant="gradient"
            size="modal"
            onClick={handleCreate}
            disabled={!title.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Next"}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
